import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, Plus, X, Loader2, Wrench, MapPin, AlertTriangle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, beDate, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { SectionCard, ListRow, EmptyState, Skeleton, Card } from "@/components/studio/StudioUI";

const EMPTY = {
  name: "", client_id: "", address: "", postal_code: "", city: "",
  building_type: "maison", year_built: "", heated_area: "", peb_class: "", access_note: "", notes: "",
};

export default function StudioSites() {
  const [params, setParams] = useSearchParams();
  const [sites, setSites] = useState(null);
  const [clients, setClients] = useState([]);
  const [options, setOptions] = useState(null);
  const [due, setDue] = useState(null);
  const [tab, setTab] = useState(params.get("entretiens") === "1" ? "entretiens" : "batiments");
  const [open, setOpen] = useState(params.get("nouveau") === "1");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    studio.get("/sites").then(({ data }) => setSites(data)).catch(() => setSites([]));
    studio.get("/maintenance/due", { params: { days: 90 } }).then(({ data }) => setDue(data)).catch(() => setDue(null));
  };

  useEffect(() => {
    load();
    studio.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    studio.get("/assets/options").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!sites) return null;
    const term = q.trim().toLowerCase();
    if (!term) return sites;
    return sites.filter((s) =>
      [s.name, s.address, s.city, s.client_name].some((v) => (v || "").toLowerCase().includes(term)),
    );
  }, [sites, q]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await studio.post("/sites", form);
      toast.success(`Bâtiment « ${data.name} » créé`);
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Création impossible"));
    } finally {
      setSaving(false);
    }
  };

  const switchTab = (value) => {
    setTab(value);
    setParams(value === "entretiens" ? { entretiens: "1" } : {});
  };

  return (
    <StudioLayout
      title="Bâtiments & équipements"
      subtitle="Le passeport technique de chaque installation : chaudière, PAC, ventilation, PEB, numéros de série, garanties et historique d'entretien."
      action={
        <button
          type="button"
          data-testid="new-site-btn"
          onClick={() => setOpen((v) => !v)}
          className={`${btnPrimary} hidden lg:inline-flex`}
        >
          {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {open ? "Fermer" : "Nouveau bâtiment"}
        </button>
      }
    >
      <div className="mt-6 flex gap-2">
        {[["batiments", "Bâtiments", sites?.length], ["entretiens", "Entretiens à programmer", due?.items?.length]].map(
          ([value, label, count]) => (
            <button
              key={value}
              type="button"
              data-testid={`sites-tab-${value}`}
              onClick={() => switchTab(value)}
              className={`inline-flex min-h-[40px] items-center gap-2 rounded-xl border px-4 text-sm font-semibold transition-colors duration-150 ${
                tab === value
                  ? "border-[#0F2645] bg-[#0F2645] text-white"
                  : "border-[#E2E7F7] bg-white text-[#5C6A87] hover:border-[#3A5CC8] hover:text-[#3A5CC8]"
              }`}
            >
              {label}
              {count ? (
                <span className={`rounded-full px-1.5 text-[11px] ${tab === value ? "bg-white/20" : "bg-slate-100"}`}>
                  {count}
                </span>
              ) : null}
            </button>
          ),
        )}
      </div>

      {open && (
        <Card className="mt-4 p-4 sm:p-5" data-testid="new-site-panel">
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              data-testid="site-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nom du bâtiment (ex. Villa Dupont, Résidence Les Tilleuls)"
              className={`${inputCls} sm:col-span-2`}
            />
            <select
              data-testid="site-client"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className={inputCls}
            >
              <option value="">Client (optionnel)</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.company || c.name}</option>
              ))}
            </select>
            <select
              data-testid="site-type"
              value={form.building_type}
              onChange={(e) => setForm({ ...form, building_type: e.target.value })}
              className={inputCls}
            >
              {(options?.building_types || []).map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <input
              data-testid="site-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Rue et numéro"
              className={`${inputCls} sm:col-span-2`}
            />
            <input
              data-testid="site-postal"
              value={form.postal_code}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              placeholder="Code postal"
              className={inputCls}
            />
            <input
              data-testid="site-city"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Commune"
              className={inputCls}
            />
            <select
              data-testid="site-peb"
              value={form.peb_class}
              onChange={(e) => setForm({ ...form, peb_class: e.target.value })}
              className={inputCls}
            >
              <option value="">Classe PEB inconnue</option>
              {(options?.peb_classes || []).filter(Boolean).map((p) => (
                <option key={p} value={p}>PEB {p}</option>
              ))}
            </select>
            <input
              data-testid="site-year"
              value={form.year_built}
              onChange={(e) => setForm({ ...form, year_built: e.target.value })}
              placeholder="Année de construction"
              className={inputCls}
            />
            <textarea
              data-testid="site-access"
              value={form.access_note}
              onChange={(e) => setForm({ ...form, access_note: e.target.value })}
              placeholder="Accès, clés, code, étage, emplacement de la chaufferie…"
              rows={2}
              className={`${inputCls} sm:col-span-2`}
            />
            <div className="flex gap-2 sm:col-span-2">
              <button type="submit" data-testid="site-submit" disabled={saving} className={btnPrimary}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Créer le bâtiment
              </button>
              <button type="button" onClick={() => setOpen(false)} className={btnGhost}>Annuler</button>
            </div>
          </form>
        </Card>
      )}

      {tab === "batiments" ? (
        <div className="mt-4">
          <input
            data-testid="sites-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un bâtiment, une adresse, un client…"
            className={`${inputCls} mb-3`}
          />
          {filtered === null ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
          ) : filtered.length === 0 ? (
            <EmptyState
              testId="sites-empty"
              icon={Building2}
              title="Aucun bâtiment enregistré"
              hint="Créez la fiche du bâtiment, puis ajoutez ses équipements. Vous garderez l'historique complet et vous pourrez replacer les entretiens automatiquement."
              action={
                <button type="button" data-testid="sites-empty-cta" onClick={() => setOpen(true)} className={`${btnPrimary} mt-2`}>
                  <Plus className="h-4 w-4" /> Nouveau bâtiment
                </button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {filtered.map((s) => (
                <Link
                  key={s.id}
                  to={`/studio/batiments/${s.id}`}
                  data-testid={`site-card-${s.id}`}
                  className="group block rounded-2xl border border-[#E2E7F7]/80 bg-white p-4 transition-all duration-150 hover:-translate-y-0.5 hover:border-[#3A5CC8]/40 hover:shadow-[0_12px_28px_-16px_rgba(11,30,61,0.25)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-heading text-base font-bold text-[#0F2645]">{s.name}</p>
                      <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-[#5C6A87]">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {[s.address, s.postal_code, s.city].filter(Boolean).join(" ") || "Adresse non renseignée"}
                      </p>
                    </div>
                    {s.due_count ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                        <AlertTriangle className="h-3 w-3" /> {s.due_count}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[#5C6A87]">{s.building_type_label}</span>
                    {s.peb_class && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">PEB {s.peb_class}</span>
                    )}
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EEF1FC] px-2 py-0.5 text-[#2F4CA8]">
                      <Wrench className="h-3 w-3" /> {s.equipment_count} équipement{s.equipment_count > 1 ? "s" : ""}
                    </span>
                    {s.client_name && <span className="truncate text-[#5C6A87]">{s.client_name}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : (
        <SectionCard
          testId="maintenance-due-list"
          className="mt-4"
          title="Entretiens à programmer"
          hint="Les 90 prochains jours, et tout ce qui est déjà en retard"
        >
          {due === null ? (
            <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-14" />)}</div>
          ) : due.items.length === 0 ? (
            <EmptyState
              testId="maintenance-empty"
              icon={CalendarClock}
              title="Aucun entretien à replacer"
              hint="Dès qu'un équipement approche de sa date d'entretien, il apparaît ici — et votre client reçoit automatiquement un rappel 30 jours avant."
            />
          ) : (
            <div className="space-y-2">
              {due.items.map((e) => (
                <ListRow
                  key={e.id}
                  to={`/studio/equipements/${e.id}`}
                  testId={`due-${e.id}`}
                  bar={e.maintenance_state === "en_retard" ? "bg-rose-500" : "bg-amber-400"}
                  title={e.name}
                  subtitle={`${[e.client_name, e.site_name, e.site_city].filter(Boolean).join(" · ")} — à faire avant le ${beDate(e.next_maintenance)}`}
                  right={
                    e.maintenance_state === "en_retard"
                      ? `${Math.abs(e.days_until_due)} j de retard`
                      : `dans ${e.days_until_due} j`
                  }
                />
              ))}
            </div>
          )}
        </SectionCard>
      )}
    </StudioLayout>
  );
}
